// TODO move this type
import type { DateOrRange, DepartureDateStore } from '@/infrastructure/departure-date/store'
import type { createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler'
import type { SearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository'
import type { SearchStore } from '@/scopes/search/search/infrastructure/store/store'
import type { FilterOption } from '@/scopes/search/types'

import type { StoreApi } from 'zustand'
import { departureDateStore as defaultDepartureDateStore } from '@/infrastructure/departure-date/store'
import { scrollToSearchElementDelayed } from '@/scopes/search/search/infrastructure/dom/scrollToSearchElement'
import { createSearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository'
import { searchStore as defaultSearchStore } from '@/scopes/search/search/infrastructure/store/store'
import { getLocaleDateString } from '@sunwebgroup/ui-components'

export interface SelectDateFilterActions {
  setDepartureDateFilterSelection: (departureDate: DateOrRange, silent?: boolean) => void
  setLastMinuteFilterSelection: (value: string | string[] | undefined, silent?: boolean) => void
  getRangeFromLastMinuteSelection: (selectedOption: FilterOption<string, { DaysToDepartureRange: string }> | undefined) => DateOrRange | undefined
}

interface Dependencies {
  searchQueryRepository: SearchQueryRepository
  searchStore: StoreApi<SearchStore>
  departureDateStore: StoreApi<DepartureDateStore>
  now: () => Date
  filterSelectionQueryParamHandler: ReturnType<typeof createFilterSelectionQueryParamHandler>
}

export function createSelectDateFilterActions(dependencies: Partial<Dependencies> = {}): SelectDateFilterActions {
  const {
    searchQueryRepository = createSearchQueryRepository(),
    searchStore = defaultSearchStore,
    departureDateStore = defaultDepartureDateStore,
    now = () => new Date(),
    filterSelectionQueryParamHandler,
  } = dependencies as Dependencies

  function setDepartureDateFilterSelection(departureDate: DateOrRange, silent: boolean = false) {
    const { setFilterSelection } = searchStore.getState()
    const { setDepartureDate } = departureDateStore.getState()

    setDepartureDate(departureDate)
    if (!silent) {
      setFilterSelection('DepartureDate', departureDate)
      setFilterSelection('Lastminute', undefined)
      filterSelectionQueryParamHandler.update()
      scrollToSearchElementDelayed()
    }
  }

  function setLastMinuteFilterSelection(value: string | string[] | undefined, silent: boolean = false) {
    const { setFilterSelection } = searchStore.getState()
    const { setFlexibleRange, setDepartureDate } = departureDateStore.getState()

    const dateRange = extractDateRangeFromLastMinuteSelection(value)

    if (dateRange) {
      setFlexibleRange('exact')
    }
    setDepartureDate(dateRange)
    if (!silent) {
      setFilterSelection('DepartureDate', dateRange)
      setFilterSelection('Lastminute', value)
      filterSelectionQueryParamHandler.update()
      scrollToSearchElementDelayed()
    }
  }

  function extractDateRangeFromLastMinuteSelection(value: string | string[] | undefined) {
    const activeQuery = searchQueryRepository.getActiveQuery()
    const lastMinuteOptions = activeQuery?.filters?.Lastminute?.options ?? []
    const selectedOption = lastMinuteOptions.find(option => option.value === value)
    return getRangeFromLastMinuteSelection(selectedOption)
  }

  function getRangeFromLastMinuteSelection(selectedOption: FilterOption<string, { DaysToDepartureRange: string }> | undefined) {
    const range = selectedOption?.metadata.DaysToDepartureRange.split('|').map(n => Number.parseInt(n, 10))
    return calculateDateRange(range, now())
  }

  function calculateDateRange(range: number[] | undefined, originDate: Date): DateOrRange | undefined {
    if (!range)
      return undefined

    const startDepartureDate = new Date(originDate)
    startDepartureDate.setUTCDate(originDate.getUTCDate() + range[0])

    const endDepartureDate = new Date(originDate)
    endDepartureDate.setUTCDate(originDate.getUTCDate() + range[1])

    return [getLocaleDateString(startDepartureDate), getLocaleDateString(endDepartureDate)]
  }

  return { setDepartureDateFilterSelection, setLastMinuteFilterSelection, getRangeFromLastMinuteSelection }
}
